import React from "react";

import { ui } from "../kits/shared";

/*
	Cuántas hay de cada talla × color de un producto nuevo, de una vez ("10 XS Blancas, 10 XS Rojas, 8 XL Blancas").
	Vacía = no se contó; 0 = se contó y no hay (Esteban, 2026-10-21). Tallas en renglones, colores en columnas; en el celular
	la tabla se desliza de lado dentro de su caja, la página no.
*/

export const cellKey = (color: string | null, size: string | null): string => (color || "").toLocaleLowerCase("es") + "|" + (size || "");

export interface MatrixCell { size: string | null; color: string | null; quantity: string }

/** Las casillas escritas (también las de 0), listas para mandar. */
export const typedCells = (sizes: string[], colors: string[], qty: Record<string, string>): MatrixCell[] => {
	const out: MatrixCell[] = [];
	for (const size of sizes.length ? sizes : [null]) {
		for (const color of colors.length ? colors : [null]) {
			const value = (qty[cellKey(color, size)] || "").trim();
			if (value !== "") out.push({ size, color, quantity: value });
		}
	}
	return out;
};

export const cellLabel = (cell: { size: string | null; color: string | null }): string => [cell.size, cell.color].filter(Boolean).join(" ") || "Piezas";

const cellInput: React.CSSProperties = {
	width: "58px", height: "38px", fontSize: "16px", textAlign: "center", padding: "0 4px",
	backgroundColor: "var(--wa-superficie)", border: "1.5px solid var(--wa-filo-2)", borderRadius: "8px"
};

const MatrizCantidades: React.FC<{
	sizes: string[]; colors: string[]; qty: Record<string, string>; busy?: boolean; readOnly?: boolean; onChange?: (qty: Record<string, string>) => void;
	/** Texto chico bajo cada casilla (lo que ya se contó). */ hints?: Record<string, string>;
	/** Casillas que no existen (esa talla no viene en ese color): no se escriben. */ missing?: Set<string>;
	/** Compras: vacía = no se pide (no hay "sin contar" ni "Lo Demás en 0"). */ ordering?: boolean;
}> = ({ sizes, colors, qty, busy, readOnly, onChange, hints, missing, ordering }) => {
	const rows: (string | null)[] = sizes.length ? sizes : [null];
	const columns: (string | null)[] = colors.length ? colors : [null];
	const cells = typedCells(sizes, colors, qty);
	const pieces = cells.reduce((sum, cell) => sum + (Number(cell.quantity) || 0), 0);
	const blanks = rows.length * columns.length - cells.length - (missing ? missing.size : 0);
	const set = (key: string, value: string) => onChange && onChange({ ...qty, [key]: value.replace(/[^\d]/g, "").slice(0, 6) });
	const zeroBlanks = () => {
		const next = { ...qty };
		rows.forEach((size) => columns.forEach((color) => { const key = cellKey(color, size); if ((next[key] || "").trim() === "" && !(missing && missing.has(key))) next[key] = "0"; }));
		onChange && onChange(next);
	};

	if (!sizes.length && !colors.length) return null;
	return <div>
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto", maxWidth: "100%" }}>
			<table style={{ borderCollapse: "collapse", width: columns.length > 2 ? "auto" : "100%" }}>
				<thead><tr>
					<th style={{ ...ui.th, position: "sticky", left: 0, background: "var(--wa-superficie)", zIndex: 1 }}>{sizes.length ? "Talla" : ""}</th>
					{columns.map((color) => <th key={color || "-"} style={{ ...ui.th, textAlign: "center", whiteSpace: "nowrap" }}>{color || (ordering ? "Cuántas" : "Cuántas hay")}</th>)}
				</tr></thead>
				<tbody>{rows.map((size) => <tr key={size || "-"}>
					<td style={{ ...ui.td, fontWeight: 700, position: "sticky", left: 0, background: "var(--wa-superficie)", zIndex: 1, whiteSpace: "nowrap" }}>{size || ""}</td>
					{columns.map((color) => {
						const key = cellKey(color, size);
						const value = qty[key] || "";
						return <td key={key} style={{ ...ui.td, textAlign: "center", padding: "4px" }}>
							{missing && missing.has(key) ? <span style={{ color: "var(--wa-apagado)" }}>·</span> : readOnly
								? <span style={{ fontWeight: value !== "" ? 700 : 400, color: value === "" ? "var(--wa-apagado)" : undefined }}>{value === "" ? "–" : value}</span>
								: <input type="text" inputMode="numeric" pattern="[0-9]*" aria-label={cellLabel({ size, color })} style={cellInput} value={value} disabled={busy}
									placeholder="–" onChange={(event) => set(key, event.target.value)} />}
							{hints && hints[key] ? <div style={{ ...ui.tiny, marginTop: "2px", whiteSpace: "nowrap" }}>{hints[key]}</div> : null}
						</td>;
					})}
				</tr>)}</tbody>
			</table>
		</div>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
			<span style={ui.small}>{pieces === 1 ? "1 pieza" : pieces + " piezas"}{cells.length ? " en " + (cells.length === 1 ? "1 casilla" : cells.length + " casillas") : ""}{blanks > 0 && !ordering ? ", " + blanks + " sin contar" : ""}</span>
			{!readOnly && !ordering && blanks > 0 ? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={zeroBlanks}>Lo Demás en 0</button> : null}
		</div>
	</div>;
};

export default MatrizCantidades;
